import styled, { css } from 'styled-components';

/**
 * Shared form-control primitive — the `.ctrl` recipe from the design system.
 *
 * One height (--h-ctrl), one radius (--r-sm), one border. Every control in
 * src/components/form/** composes these so that a text input, a textarea, the
 * custom select, the antd date/time picker and a search box are the same
 * object at rest, on hover, on focus, disabled and in error.
 *
 * `ctrlBase` is applied to the element that draws the box — which is not
 * always the one the component owns (antd renders its own <input>), so it is
 * exported as a chunk rather than baked into a styled component.
 */

export const ctrlBase = css`
  width: 100%;
  height: var(--h-ctrl);
  padding: 0 var(--s3);
  background: var(--surface);
  border: 1px solid var(--line-strong);
  border-radius: var(--r-sm);
  font-family: var(--font-ui);
  font-size: var(--fs-body);
  font-weight: var(--fw-regular);
  line-height: var(--lh-body);
  color: var(--text);
  transition:
    border-color 0.12s,
    box-shadow 0.12s;

  &::placeholder {
    color: var(--text-faint);
  }

  &:hover:not(:disabled) {
    border-color: var(--text-faint);
  }

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px var(--primary-soft);
  }

  &:disabled,
  &.disabled {
    background: var(--surface-alt);
    color: var(--text-faint);
    cursor: not-allowed;
  }

  /* Arabic side of a bilingual pair — see Step 6. */
  &[dir='rtl'] {
    font-family: var(--font-ar);
    text-align: right;
  }

  /*
   * Chrome paints an autofilled field #E8F0FE and will not take a plain
   * background — the only thing that reaches it is a very long inset shadow.
   * Without this the login screen greets everyone with two pale blue boxes
   * that match nothing else in the product, which is a poor first impression
   * for a field the panel styled correctly.
   */
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-text-fill-color: var(--text);
    -webkit-box-shadow: 0 0 0 1000px var(--surface) inset;
    caret-color: var(--text);
  }

  &:-webkit-autofill:focus {
    -webkit-box-shadow:
      0 0 0 1000px var(--surface) inset,
      0 0 0 3px var(--primary-soft);
  }
`;

/** Multi-line variant. A textarea is an input, not prose — --lh-body. */
export const ctrlMultiline = css`
  height: auto;
  min-height: 96px;
  padding: var(--s2) var(--s3);
  line-height: var(--lh-body);
  resize: vertical;
`;

/**
 * Error state. Applied from the container, which is where every control in
 * this codebase already puts its `error` / `success` class.
 */
export const ctrlStates = css`
  &.error {
    input,
    textarea,
    .ant-picker-input input {
      border-color: var(--danger);
      background: var(--danger-tint);
    }

    input:focus,
    textarea:focus,
    .ant-picker-input input:focus {
      border-color: var(--danger);
      box-shadow: 0 0 0 3px var(--danger-soft);
    }
  }

  &.success {
    input,
    textarea,
    .ant-picker-input input {
      border-color: var(--ok);
    }

    input:focus,
    textarea:focus,
    .ant-picker-input input:focus {
      border-color: var(--ok);
      box-shadow: 0 0 0 3px var(--ok-soft);
    }
  }
`;

export const fieldContainer = css`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  ${ctrlStates}
`;

export const fieldLabel = css`
  display: flex;
  align-items: center;
  gap: var(--s2);
  margin-bottom: var(--s2);
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  line-height: var(--lh-body);
  color: var(--text-muted);
  cursor: pointer;
`;

/**
 * The design's `.lang-tag` — which language side of a bilingual pair a field
 * is. Six entities are authored through paired en_* / ar_* fields, and this
 * is what lets both labels read "Item title" rather than "English Title" and
 * "Arabic Title", which are different lengths and so never lined up.
 */
export const LangTag = styled.span`
  font-family: var(--font-mono);
  /* Design says 9.5px / 3px radius; neither is on a scale. Rounded to the
     smallest type size and the small radius, per the 14px ruling in FINDINGS
     — a seventh type size for one styled component does not earn its place,
     and the difference is invisible at mono medium. */
  font-size: var(--fs-th);
  font-weight: var(--fw-medium);
  line-height: var(--lh-body);
  letter-spacing: 0.03em;
  padding: 0 var(--s1);
  border-radius: var(--r-sm);
  background: var(--neutral-soft);
  color: var(--text-faint);
`;

/** The design's `.req` — the required-field asterisk. */
export const Req = styled.span`
  color: var(--danger);
`;

export const fieldMessage = css`
  display: flex;
  align-items: center;
  gap: var(--s1);
  margin-top: var(--s1);
  font-size: var(--fs-micro);
  font-weight: var(--fw-regular);
  line-height: var(--lh-body);
  color: var(--text-faint);

  &.error-message {
    color: var(--danger);
  }

  &.success-message {
    color: var(--ok);
  }
`;
